import React, { FC, useEffect, useMemo, useState } from 'react'
import Box from '@components/Box'
import Control from '@components/CalcAero/Control'
import Contributions from '@components/CalcAero/Contributions'
import { formatMoney, formatYears } from '@components/InputSlider/helpers'
import { TTabs, Tabs, IValues } from '@components/CalcAero/types'
import useCalcAero from '@components/CalcAero/hooks/useCalcAero'
import { retirementAges } from '@components/Cacl/data'
import {
  Divider,
  SButtonConclude,
  SButtonTab,
  SCalcAero,
  SCalcCaption,
  SContractForm,
  SDisclaimerContainer,
  SDisclaimerText,
  SResultTitle,
  SSum,
  SSumCaption,
  SAttentionIcon,
  SOverlay,
} from './styled'

export const defaultValues: IValues = {
  gender: 'm',
  age: 30,
  monthlyPayment: 10000,
  period: 10,
}

const CalcAero: FC = () => {
  const {
    gender,
    age,
    monthlyPayment,
    period,
    setGender,
    setAge,
    setMonthlyPayment,
    setPeriod,
    activeTab,
    setActiveTab,
    pensionCapital,
    totalMonthlyPensionAmount,
    inTotalYouWillAccumulate,
    numberOfYearsBeforeRetirement,
    yourContributionsForFiveYears,
    yourProfit,
    yourContributionsAmount,
    inTotalYouWillAccumulateAero,
    contributionsForFiveYearsAero,
    yourProfitAero,
    aeroMonthlyPensionAmount,
  } = useCalcAero(defaultValues)

  const tabs = useMemo<TTabs>(
    () => ({
      [Tabs.capital]: 'Пенсионный капитал через 5 лет',
      [Tabs.payment]: `Пенсионная выплата в ${formatYears(retirementAges[gender])}`,
    }),
    [gender],
  )

  return (
    <Box>
      <SCalcAero>
        <Box width={{ _: '100%', xl: '348px' }} flexGrow={0} flexShrink={0}>
          <Control
            gender={gender}
            age={age}
            monthlyPayment={monthlyPayment}
            period={period}
            setGender={setGender}
            setAge={setAge}
            setMonthlyPayment={setMonthlyPayment}
            setPeriod={setPeriod}
          />
          <SDisclaimerContainer hidden={aeroMonthlyPensionAmount >= 250}>
            <SAttentionIcon />
            <SDisclaimerText>
              Размер пенсии ниже минимального, требуется увеличить взнос или уменьшить период выплаты пенсии
            </SDisclaimerText>
          </SDisclaimerContainer>
        </Box>

        <Divider />

        <Box mb={{ xl: '18px' }} display={{ xl: 'flex' }} flexDirection={{ xl: 'column' }} width="100%">
          <Box display="flex" flexDirection={{ _: 'column', md: 'row' }} mb={{ _: '40px', xl: '32px' }}>
            {(Object.keys(tabs) as Tabs[]).map((tab, index) => (
              <SButtonTab key={index} onClick={() => setActiveTab(Tabs[tab])} active={tab === activeTab}>
                {tabs[tab]}
              </SButtonTab>
            ))}
          </Box>

          <Box position="relative">
            <SOverlay hidden={aeroMonthlyPensionAmount >= 250} />
            <Box width="100%">
              <SResultTitle>
                {activeTab === Tabs.capital
                  ? 'Через 5 лет Ваш пенсионный капитал составит:'
                  : `При назначении пенсионной выплаты в ${retirementAges[gender]} лет ожидаемый размер Вашей ежемесячной пенсии в течение ${period} лет составит:`}
              </SResultTitle>
              <SSum>{formatMoney(activeTab === Tabs.capital ? pensionCapital : totalMonthlyPensionAmount)}</SSum>
              {activeTab === Tabs.payment && (
                <SSumCaption>Размер пенсии, назначенной из взносов работодателя указан за вычетом НДФЛ</SSumCaption>
              )}
            </Box>

            <Contributions
              title="Ваши взносы с учетом инвестиционного дохода от СберНПФ"
              resultsData={[
                { title: 'Всего вы накопите', value: inTotalYouWillAccumulate },
                {
                  title: `Ваши взносы за ${formatYears(
                    activeTab === Tabs.capital ? 5 : numberOfYearsBeforeRetirement,
                  )}`,
                  value: activeTab === Tabs.capital ? yourContributionsForFiveYears : yourContributionsAmount,
                },
                { title: 'Ваш доход', value: yourProfit },
              ]}
            />

            <Contributions
              title="Взносы работодателя 20% с учетом инвестиционного дохода от СберНПФ"
              resultsData={[
                { title: 'Всего вы накопите', value: inTotalYouWillAccumulateAero },
                {
                  title: `Взносы работодателя за ${formatYears(
                    activeTab === Tabs.capital ? 5 : numberOfYearsBeforeRetirement,
                  )}`,
                  value: contributionsForFiveYearsAero,
                },
                { title: 'Ваш доход', value: yourProfitAero },
              ]}
            />

            <Box display={{ _: 'flex' }} flexDirection={{ _: 'column', md: 'row' }} mt={{ _: '4px', xl: '24px' }}>
              <SButtonConclude
                as="a"
                href="https://npo.npfsb.ru/?uniguid=AF2072&utm_source=sbernpfsite&utm_medium=lpdzo&utm_campaign=getkppcontract"
                target="_blank"
              >
                Оформить договор онлайн
              </SButtonConclude>
              <SContractForm as="a" href="https://npfsberbanka.ru/upload/aero-landing-contract.pdf" target="_blank">
                Форма договора оферты
              </SContractForm>
            </Box>
          </Box>
        </Box>
      </SCalcAero>
      <SCalcCaption>
        Не является офертой и не гарантирует доходность в будущем. Ожидаемая доходность в размере 7.5% годовых.
      </SCalcCaption>
    </Box>
  )
}

export default CalcAero
